// frontend/src/hooks/useMonthlyLeaveUsageReport.js
import { useMutation, useQuery } from '@tanstack/react-query';
import { reportingAPI } from '../services/api';
import toast from '../utils/toast';

const buildParams = (asOfMonth, departmentIds = []) => {
  const params = { as_of_month: asOfMonth };
  if (departmentIds.length > 0) {
    params.department_ids = departmentIds.join(',');
  }
  return params;
};

const triggerBlobDownload = (response, fallbackName) => {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || fallbackName;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const useMonthlyLeaveUsageReport = (asOfMonth, departmentIds = []) => {
  return useQuery({
    queryKey: ['reports', 'monthly-leave-usage', { asOfMonth, departmentIds }],
    queryFn: async () => {
      const response = await reportingAPI.getMonthlyLeaveUsage(
        buildParams(asOfMonth, departmentIds),
      );
      return response.data;
    },
    enabled: Boolean(asOfMonth),
    staleTime: 2 * 60 * 1000,
  });
};

export const useExportMonthlyLeaveUsage = () => {
  return useMutation({
    mutationFn: async ({ asOfMonth, departmentIds = [] }) => {
      const response = await reportingAPI.exportMonthlyLeaveUsage(
        buildParams(asOfMonth, departmentIds),
      );
      triggerBlobDownload(
        response,
        'Monthly Leave Usage.xlsx',
      );
      return true;
    },
    onSuccess: () => {
      toast.success('Excel file downloaded');
    },
    onError: (error) => {
      const msg = error.response?.data?.error || 'Failed to export report';
      toast.error(typeof msg === 'string' ? msg : 'Failed to export report');
    },
  });
};
